import React from 'react';
import { estadoLinea, fmtPct, fmtUSD, textoCriollo, unidadDia, unidadDias } from './proyeccionUtils';

// Barra de avance: facturado (sólido) + pendientes (tenue) hasta el piso, con
// marcas para el objetivo y el forecast.
// Color de la barra: el del semáforo, o azul cuando no hay objetivo (el gris
// de "sin objetivo" no se distinguiría del fondo de la barra).
export const colorBarra = (estado) => (estado.color === 'var(--text-3)' ? 'var(--blue)' : estado.color);

// Guiones dibujados a mano: un `border: dashed` en una marca tan corta se ve
// como línea continua y se confunde con la del objetivo.
const GUIONES = 'repeating-linear-gradient(to bottom, var(--text-2) 0 3px, transparent 3px 5px)';

// Leyenda: cada etiqueta lleva la misma marca con la que aparece en la barra.
export function Marca({ tipo, color }) {
  const base = { display: 'inline-block', flexShrink: 0, verticalAlign: 'middle' };
  if (tipo === 'objetivo') return <span style={{ ...base, width: 3, height: 11, borderRadius: 2, background: 'var(--text)' }} />;
  if (tipo === 'forecast') return <span style={{ ...base, width: 2, height: 13, background: GUIONES }} />;
  if (tipo === 'facturado') return <span style={{ ...base, width: 10, height: 10, borderRadius: 2, background: color }} />;
  if (tipo === 'piso') {
    return (
      <span style={{ ...base, position: 'relative', width: 10, height: 10, borderRadius: 2, overflow: 'hidden', background: 'var(--surface-3)' }}>
        <span style={{ position: 'absolute', inset: 0, background: color, opacity: 0.28 }} />
      </span>
    );
  }
  return null;
}

const etiqueta = (texto, marca, color, size) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
    <Marca tipo={marca} color={color} />
    <span style={{ fontSize: size }}>{texto}</span>
  </span>
);

export function BarraAvance({ linea, color }) {
  const { objetivo, real, piso, forecast } = linea;
  const escala = Math.max(objetivo || 0, piso || 0, forecast || 0, 1) * 1.04;
  const pct = (v) => `${Math.min(100, Math.max(0, (v / escala) * 100))}%`;

  return (
    <div style={{ position: 'relative', height: 30, marginTop: 4 }}>
      <div style={{ position: 'absolute', top: 8, left: 0, right: 0, height: 12, borderRadius: 6, background: 'var(--surface-3)', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, width: pct(piso), background: color, opacity: 0.28 }} />
        <div style={{ position: 'absolute', inset: 0, width: pct(real), background: color }} />
      </div>
      {forecast != null && (
        <div title={`Forecast: ${fmtUSD(forecast)}`}
             style={{ position: 'absolute', top: 4, left: pct(forecast), width: 2, height: 21, background: GUIONES }} />
      )}
      {!!objetivo && (
        <div title={`Objetivo: ${fmtUSD(objetivo)}`}
             style={{ position: 'absolute', top: 2, left: pct(objetivo), width: 3, height: 24, marginLeft: -1, borderRadius: 2, background: 'var(--text)' }} />
      )}
    </div>
  );
}

function Dato({ label, marca, color, valor, detalle, detalleColor, title }) {
  return (
    <div title={title} style={{ minWidth: 0 }}>
      <div style={{ color: 'var(--text-3)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 3 }}>
        {etiqueta(label, marca, color, 11)}
      </div>
      <div style={{ fontSize: 15, fontWeight: 600, whiteSpace: 'nowrap' }}>{valor}</div>
      {detalle && <div style={{ fontSize: 12, color: detalleColor || 'var(--text-2)', marginTop: 1 }}>{detalle}</div>}
    </div>
  );
}

/**
 * Versión de una fila para el dashboard: estado | ritmo necesario | barra + números.
 * El texto "en criollo" va como tooltip para no sumar altura.
 */
export function LineaCompacta({ linea, titulo, subtitulo, pocoDato, propia = true }) {
  const estado = estadoLinea(linea, pocoDato);
  const alcanzado = linea.objetivo_alcanzado;
  const heroLabel = alcanzado ? 'Promedio diario actual' : (propia ? 'Tu objetivo de hoy' : 'Ritmo necesario');
  const heroValor = alcanzado ? linea.ritmo_actual : linea.ritmo_necesario;
  const restantes = linea.dias?.restantes;

  const barra = colorBarra(estado);
  const dato = (label, marca, valor, detalle, detalleColor, title) => (
    <div title={title} style={{ minWidth: 0 }}>
      <div style={{ color: 'var(--text-3)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.05em' }}>
        {etiqueta(label, marca, barra, 10)}
      </div>
      <div style={{ fontSize: 14, fontWeight: 600, whiteSpace: 'nowrap' }}>{valor}</div>
      {detalle && <div style={{ fontSize: 11, color: detalleColor || 'var(--text-2)', whiteSpace: 'nowrap' }}>{detalle}</div>}
    </div>
  );

  return (
    <div className="proy-fila" title={textoCriollo(linea, { propia })}
         style={{ display: 'grid', gridTemplateColumns: 'minmax(120px, 150px) minmax(170px, 210px) minmax(0, 1fr)', gap: 20, alignItems: 'center' }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 600 }}>{titulo}</div>
        {subtitulo && <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 4 }}>{subtitulo}</div>}
        <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 100, background: estado.dim, color: estado.color, whiteSpace: 'nowrap' }}>
          {estado.label}
        </span>
      </div>

      <div style={{ background: 'var(--surface-2)', borderRadius: 10, padding: '8px 12px', minWidth: 0 }}>
        <div style={{ fontSize: 11, color: 'var(--text-2)' }}>{heroLabel}</div>
        <div style={{ fontSize: 20, fontWeight: 700, lineHeight: 1.25, whiteSpace: 'nowrap' }}>
          {linea.objetivo > 0 && heroValor != null ? fmtUSD(heroValor) : '—'}
          <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-2)' }}> / {unidadDia(linea)}</span>
        </div>
        {!alcanzado && linea.faltante != null && restantes != null && (
          <div style={{ fontSize: 11, color: 'var(--text-3)', whiteSpace: 'nowrap' }}>
            Faltan {fmtUSD(linea.faltante)} en {restantes.toLocaleString('es-AR', { maximumFractionDigits: 1 })} {unidadDias(linea)}
          </div>
        )}
      </div>

      <div style={{ minWidth: 0 }}>
        <BarraAvance linea={linea} color={barra} />
        <div className="proy-datos" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 12, marginTop: 2 }}>
          {dato('Objetivo', 'objetivo', linea.objetivo ? fmtUSD(linea.objetivo) : '—', null, null,
                'Línea continua en la barra')}
          {dato('Facturado', 'facturado', fmtUSD(linea.real), linea.objetivo ? `${Math.round((linea.real / linea.objetivo) * 100)}% del obj.` : null, null,
                'Tramo de color pleno en la barra')}
          {dato('Forecast', 'forecast', fmtUSD(linea.forecast),
                linea.forecast_vs_obj != null ? `${fmtPct(linea.forecast_vs_obj)} vs obj.` : null,
                pocoDato ? 'var(--text-3)' : estado.color,
                'Línea punteada en la barra: cómo se cierra el mes si se sigue facturando a la velocidad actual')}
          {dato('Piso', 'piso', fmtUSD(linea.piso), linea.pendiente ? `+${fmtUSD(linea.pendiente)} pend.` : 'sin pendientes', null,
                'Tramo de color tenue en la barra: facturado + pedidos pendientes con entrega este mes')}
        </div>
      </div>
    </div>
  );
}

/**
 * Una línea de la proyección (un vendedor o el total de la empresa).
 * `propia` cambia el tono del texto ("llevás" vs "van").
 */
export default function LineaProyeccion({ linea, titulo, subtitulo, pocoDato, propia = true }) {
  const estado = estadoLinea(linea, pocoDato);
  const dias = linea.dias;
  const alcanzado = linea.objetivo_alcanzado;
  const heroLabel = alcanzado ? 'Promedio diario actual' : (propia ? 'Tu objetivo de hoy' : 'Ritmo necesario');
  const heroValor = alcanzado ? linea.ritmo_actual : linea.ritmo_necesario;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 600 }}>{titulo}</div>
          {subtitulo && <div style={{ fontSize: 12, color: 'var(--text-3)' }}>{subtitulo}</div>}
        </div>
        <span style={{ fontSize: 12, fontWeight: 600, padding: '3px 10px', borderRadius: 100, background: estado.dim, color: estado.color }}>
          {estado.label}
        </span>
      </div>

      {linea.objetivo > 0 && (
        <div style={{ background: 'var(--surface-2)', borderRadius: 10, padding: '12px 14px' }}>
          <div style={{ fontSize: 12, color: 'var(--text-2)' }}>{heroLabel}</div>
          <div style={{ fontSize: 26, fontWeight: 700, lineHeight: 1.2 }}>
            {heroValor != null ? fmtUSD(heroValor) : '—'}
            <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-2)' }}> / {unidadDia(linea)}</span>
          </div>
          {dias && !alcanzado && (
            <div style={{ fontSize: 12, color: 'var(--text-3)' }}>
              Faltan {fmtUSD(linea.faltante)} en {dias.restantes.toLocaleString('es-AR', { maximumFractionDigits: 1 })} {unidadDias(linea)}
            </div>
          )}
        </div>
      )}

      <BarraAvance linea={linea} color={colorBarra(estado)} />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(118px, 1fr))', gap: 12 }}>
        <Dato label="Objetivo" marca="objetivo" valor={linea.objetivo ? fmtUSD(linea.objetivo) : '—'}
              title="Línea continua en la barra" />
        <Dato label="Facturado" marca="facturado" color={colorBarra(estado)} valor={fmtUSD(linea.real)}
              title="Tramo de color pleno en la barra"
              detalle={linea.objetivo ? `${Math.round((linea.real / linea.objetivo) * 100)}% del objetivo` : null} />
        <Dato label="Forecast" marca="forecast" valor={fmtUSD(linea.forecast)}
              title="Línea punteada en la barra: cómo se cierra el mes si se sigue facturando a la velocidad actual"
              detalle={linea.forecast_vs_obj != null ? `${fmtPct(linea.forecast_vs_obj)} vs objetivo` : null}
              detalleColor={pocoDato ? 'var(--text-3)' : estado.color} />
        <Dato label="Piso comprometido" marca="piso" color={colorBarra(estado)} valor={fmtUSD(linea.piso)}
              title="Tramo de color tenue en la barra: facturado + pedidos pendientes con entrega este mes"
              detalle={linea.pendiente ? `+${fmtUSD(linea.pendiente)} pendiente` : 'sin pendientes'} />
      </div>

      <div style={{ fontSize: 13, color: 'var(--text-2)', lineHeight: 1.5 }}>
        {textoCriollo(linea, { propia })}
      </div>
    </div>
  );
}
